import { produce } from 'immer'

import { formatTime } from '@/utils/helpers'

import * as TASK_TYPES from './tasks.constants'
import { INBOX_PAGE_LIMIT } from './tasks.constants'

// Action Types
export const SET_TASK = 'tasks/setTask'
export const SET_TASKS = 'tasks/setTasks'
export const SET_DATA = 'tasks/setData'
export const SET_FILTER = 'tasks/setFilter'
export const SET_LOADING = 'tasks/setLoading'
export const SET_ERROR = 'tasks/setError'
export const SET_FILTERS_LOADED = 'tasks/setFiltersLoaded'
export const SET_SAVED_FILTER_ID = 'tasks/setSavedFilterId'
export const ADD_TO_CALL_ID_DELETE_QUEUE = 'tasks/addToCallIdDeleteQueue'
export const REMOVE_FROM_CALL_ID_DELETE_QUEUE = 'tasks/removeFromCallIdDeleteQueue'
export const SET_CALL_ID_DELETE_COMPLETED = 'tasks/setCallIdDeleteCompleted'
export const SET_CALL_COUNT_INCREASED = 'tasks/setCallCountIncreased'
export const RESET_CALL_QUEUES = 'tasks/resetCallIdDeleteQueue'
export const SET_PAGINATED_TASKS = 'tasks/setPaginatedTasks'
export const RESET_TASK_LIST = 'tasks/resetTaskList'

// Action Creators
export const setTask = (payload) => ({ type: SET_TASK, payload })
export const setTasks = (payload) => ({ type: SET_TASKS, payload })
export const setData = (payload) => ({ type: SET_DATA, payload })
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setFilter = (payload) => ({ type: SET_FILTER, payload })
export const setFiltersLoaded = (payload) => ({ type: SET_FILTERS_LOADED, payload })
export const setSavedFilterId = (payload) => ({ type: SET_SAVED_FILTER_ID, payload })
export const addToCallIdDeleteQueue = (payload) => ({ type: ADD_TO_CALL_ID_DELETE_QUEUE, payload })
export const removeFromCallIdDeleteQueue = (payload) => ({ type: REMOVE_FROM_CALL_ID_DELETE_QUEUE, payload }) // prettier-ignore
export const setCallIdDeleteCompleted = (payload) => ({ type: SET_CALL_ID_DELETE_COMPLETED, payload }) // prettier-ignore
export const setCallCountIncreased = () => ({ type: SET_CALL_COUNT_INCREASED })
export const resetCallQueues = () => ({ type: RESET_CALL_QUEUES })
export const setPaginatedTasks = (payload) => ({ type: SET_PAGINATED_TASKS, payload })
export const resetTaskList = () => ({ type: RESET_TASK_LIST })

const startDate = formatTime(new Date(), 'yyyy-MM-DDT00:00:00Z')
const endDate = formatTime(new Date(), 'yyyy-MM-DDT23:59:59Z')

// State
export const initialState = {
  tasks: [],
  task: null, // Warning: cobbled together JSONB nonsense
  filtersLoaded: false,
  selectedSavedFilterId: null,
  data: { agents: [], tags: [], managerAudit: [] },
  filters: {
    startDate,
    endDate,
    agents: [],
    organizationId: null,
  },
  loading: {
    coachingRequest: false,
    agents: false,
    tags: false,
    task: false,
    [TASK_TYPES.INBOX]: false,
    managerAudit: false,
  },
  error: {
    task: false,
    [TASK_TYPES.INBOX]: false,
  },
  callIdDeleteQueue: [],
  callIdDeleteCompleted: [],
  callCountIncreased: false,
  pagination: {
    page: 1,
    limit: INBOX_PAGE_LIMIT,
    totalCount: 0,
    totalPages: 0,
  },
  counts: {
    inbox: 0,
    archive: 0,
  },
  types: [],
}

// Reducer
export default function tasksRedux(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_TASK:
        newState.task = payload
        break
      case SET_TASKS:
        newState.tasks = payload
        break
      case SET_DATA:
        newState.data = { ...state.data, ...action.payload }
        break
      case SET_FILTER:
        newState.filters = { ...state.filters, ...action.payload }
        break
      case SET_LOADING:
        newState.loading = { ...state.loading, ...payload }
        break
      case SET_ERROR:
        newState.error = { ...state.error, ...payload }
        break
      case SET_FILTERS_LOADED:
        newState.filtersLoaded = payload
        break
      case SET_SAVED_FILTER_ID:
        newState.selectedSavedFilterId = payload
        break
      case ADD_TO_CALL_ID_DELETE_QUEUE:
        if (!state.callIdDeleteQueue.includes(payload)) {
          newState.callIdDeleteQueue.push(payload)
        }
        break
      case REMOVE_FROM_CALL_ID_DELETE_QUEUE:
        newState.callIdDeleteQueue = state.callIdDeleteQueue.filter((id) => id !== payload)
        break
      case SET_CALL_ID_DELETE_COMPLETED:
        if (!state.callIdDeleteCompleted.includes(payload)) {
          newState.callIdDeleteCompleted.push(payload)
        }
        if (state.callIdDeleteQueue.includes(payload)) {
          newState.callIdDeleteQueue = state.callIdDeleteQueue.filter((id) => id !== payload)
        }
        break
      case SET_CALL_COUNT_INCREASED:
        newState.callCountIncreased = true
        break
      case RESET_CALL_QUEUES:
        newState.callIdDeleteQueue = []
        newState.callIdDeleteCompleted = []
        newState.callCountIncreased = false
        break
      case SET_PAGINATED_TASKS:
        newState.tasks = payload.tasks || []
        newState.pagination = {
          page: payload.page || 1,
          limit: payload.limit || INBOX_PAGE_LIMIT,
          totalCount: payload.total_count || 0,
          totalPages: payload.total_pages || 0,
        }
        newState.counts = {
          inbox: payload.inbox_count || 0,
          archive: payload.archive_count || 0,
        }
        newState.types = payload.types || []
        break
      case RESET_TASK_LIST:
        // The list is shared by every inbox; drop it (and what describes it) so a newly
        // mounted inbox shows its loader instead of the previous inbox's rows. A fetch
        // always follows a reset, and the full-page loader only shows while loading AND
        // empty, so flag loading here too: otherwise the emptied list paints as "no
        // tasks" until that fetch's own setLoading lands.
        newState.tasks = initialState.tasks
        newState.pagination = initialState.pagination
        newState.counts = initialState.counts
        newState.types = initialState.types
        newState.loading = { ...state.loading, [TASK_TYPES.INBOX]: true }
        break
      default:
        break
    }
  })
}
